import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Sparkle from 'react-sparkle';
import './BadgePage.css';

const BadgePage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (gameId) {
      fetchBadgeData();
    }
  }, [gameId]);

  const fetchBadgeData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/badge/${gameId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Score not found');
        } else {
          setError('Failed to load badge data');
        }
        return;
      }

      const data = await response.json();
      setBadgeData(data.badge);
    } catch (err) {
      console.error('Error fetching badge data:', err);
      setError('Failed to load badge data');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayGame = () => {
    navigate('/game');
  };

  const handleViewLeaderboard = () => {
    navigate('/leaderboard');
  };

  const handleShare = async () => {
    if (navigator.share && badgeData) {
      try {
        await navigator.share({
          title: `${badgeData.playerName}'s Memory Game Achievement`,
          text: `Check out ${badgeData.playerName}'s ${badgeData.placement === 1 ? '1st' : 
                 badgeData.placement === 2 ? '2nd' : 
                 badgeData.placement === 3 ? '3rd' : 
                 `#${badgeData.placement}`} place score of ${badgeData.score} in the Memory Game!`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled or failed:', err);
        // Fallback to copying URL
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert('Badge link copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Badge link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="badge-page">
        <div className="badge-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading badge...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !badgeData) {
    return (
      <div className="badge-page">
        <div className="badge-container">
          <div className="error-state">
            <h1>❌ {error || 'Badge not found'}</h1>
            <p>The score you're looking for doesn't exist or has been removed.</p>
            <button className="play-btn" onClick={handlePlayGame}>
              🎮 Play Memory Game
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="badge-page">
      <Helmet>
        <title>{badgeData.playerName}'s Memory Game Achievement - {badgeData.score}</title>
        <meta name="description" content={`${badgeData.playerName} completed the Memory Game in ${badgeData.score} and achieved ${badgeData.placement === 1 ? '1st' : 
          badgeData.placement === 2 ? '2nd' : 
          badgeData.placement === 3 ? '3rd' : 
          `#${badgeData.placement}`} place! Play now to beat their score.`} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={`${badgeData.playerName}'s Memory Game Achievement`} />
        <meta property="og:description" content={`Completed in ${badgeData.score} - ${badgeData.placement === 1 ? '1st' : 
          badgeData.placement === 2 ? '2nd' : 
          badgeData.placement === 3 ? '3rd' : 
          `#${badgeData.placement}`} place! Can you beat this score?`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content={badgeData.imageUrl} />
        <meta property="og:image:width" content="400" />
        <meta property="og:image:height" content="200" />
        <meta property="og:image:alt" content={`${badgeData.playerName}'s Memory Game Badge`} />
        <meta property="og:site_name" content="NicoleCodeGirl Memory Game" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${badgeData.playerName}'s Memory Game Achievement`} />
        <meta name="twitter:description" content={`Completed in ${badgeData.score} - ${badgeData.placement === 1 ? '1st' : 
          badgeData.placement === 2 ? '2nd' : 
          badgeData.placement === 3 ? '3rd' : 
          `#${badgeData.placement}`} place!`} />
        <meta name="twitter:image" content={badgeData.imageUrl} />
      </Helmet>

      <div className="badge-container">
        <Sparkle
          color="pink"
          count={15}
          minSize={3}
          maxSize={8}
          overflowPx={10}
          fadeOutSpeed={10}
          flicker={false}
        />
        <Sparkle
          color="#00ff00"
          count={10}
          minSize={2}
          maxSize={6}
          overflowPx={15}
          fadeOutSpeed={15}
          flicker={true}
        />
        <Sparkle
          color="#ffd700"
          count={8}
          minSize={4}
          maxSize={10}
          overflowPx={5}
          fadeOutSpeed={8}
          flicker={false}
        />
        <div className="badge-header">
          <h1 className="badge-page-title">🏆 Achievement Badge</h1>
        </div>
        
        <div className="game-name">
          <h2>NicoleCodeGirl Memory Game</h2>
        </div>

        <div className="badge-display">
          <div className="badge-details">
            <h2 className="player-name">{badgeData.playerName}</h2>
            <div className="score-info">
              <div className="score-item">
                <span className="label">Score:</span>
                <span className="value">{badgeData.score}</span>
              </div>
              <div className="score-item">
                <span className="label">Place:</span>
                <span className="value">
                  {badgeData.placement === 1 ? '🥇 1st Place' : 
                   badgeData.placement === 2 ? '🥈 2nd Place' : 
                   badgeData.placement === 3 ? '🥉 3rd Place' : 
                   `#${badgeData.placement} Place`}
                </span>
              </div>
              <div className="score-item">
                <span className="label">Theme:</span>
                <span className="value">{badgeData.theme}</span>
              </div>
              <div className="score-item">
                <span className="label">Date:</span>
                <span className="value">{badgeData.date}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="badge-actions">
          <button className="action-btn primary" onClick={handleShare}>
            📤 Share Badge
          </button>
        </div>

        <div className="badge-footer">
          <p>Think you can beat this score? Play the Memory Game now!</p>
          <a href="/" className="game-link">memory-game.nicolecodegirl.com</a>
        </div>
      </div>
    </div>
  );
};

export default BadgePage;
